import React, { useState, useEffect, Fragment } from 'react'
import _ from 'lodash'
import moment from 'moment'
import { billingFrequencies } from '@evergreen/shared/constants'
import {
  getBillingCycleDateValue,
  forEachPromise,
  getPrettyBillingRange,
  switchy,
} from '../utils'
import api from '../api'
import { watchBillingHistory, getClientIdsToBeBilled } from '../fire'
import Dropdown from './Dropdown'
import toast from './Toast'

let getBillingCycleOptions = billingFrequency =>
  _.chain(12) //last 12 months of billingCycles
    .times(m => {
      let mo = moment().subtract(m, 'months')
      let billingCycle = mo.date() <= 14 ? 0 : 1
      let month = mo.month()
      let year = mo.year()

      return switchy(billingFrequency, {
        monthly: () =>
          getBillingCycleDateValue({
            year,
            month,
            billingFrequency,
          }),
        semimonthly: () => {
          if (m === 0 && billingCycle === 0) {
            // current month is in billingCycle 0, so don't include billingCycle 1
            return getBillingCycleDateValue({ month, year, billingCycle: 0 })
          }

          return [
            getBillingCycleDateValue({ month, year, billingCycle: 1 }),
            getBillingCycleDateValue({ month, year, billingCycle: 0 }),
          ]
        },
      })
    })
    .flatten()
    .value()

let Billing = () => {
  let [billingFrequency, setBillingFrequency] = useState(
    billingFrequencies.semimonthly
  )
  let [billingCycleOptions, setBillingCycleOptions] = useState(
    getBillingCycleOptions(billingFrequency)
  )
  let [billingCycleDateValue, setBillingCycleDateValue] = useState(
    billingCycleOptions[1]
  )

  let [sendingInvoices, setSendingInvoices] = useState(false)
  let [reauthorizeLink, setReauthorizeLink] = useState('')
  let [billingHistory, setBillingHistory] = useState([])
  let [clientId, setClientId] = useState('')

  useEffect(() => {
    let billingHistoryOff = watchBillingHistory(h => setBillingHistory(h))

    return () => {
      billingHistoryOff()
    }
  }, [])

  let sendSingleInvoice = async clientIdToInvoice => {
    let throwIt = false

    try {
      let { success, message, refreshTokenExpired, authUri } = await api(
        '/quickbooks/send-invoices',
        {
          billingCycleDateValue,
          clientId: clientIdToInvoice,
        }
      )

      if (refreshTokenExpired) {
        throwIt = true
        setReauthorizeLink(authUri)
      } else if (success) {
        toast.success(message)
      } else if (!success) {
        toast.error(`Error: ${message}`, { autoClose: false })
      }
    } catch (e) {
      toast.error(
        'There was an internal error triggering the sending of invoices',
        { autoClose: false }
      )
    }

    if (throwIt) {
      throw new Error('Should not continue with next invoice')
    }
  }

  let sendAllInvoices = async () => {
    let startedToastId = toast.info(
      'Sending of invoices is now in progress.  Please do not leave the Billing page until this has completed.',
      { autoClose: false }
    )

    try {
      setSendingInvoices(true)
      let clientIdsToBill = await getClientIdsToBeBilled(billingFrequency)
      await forEachPromise(clientIdsToBill, clientIdToInvoice => {
        return sendSingleInvoice(clientIdToInvoice)
      })
      toast.success('Invoices are done being sent.  Have a nice day.', {
        autoClose: 15000,
      })
    } catch (e) {
      // do nothing, just stop.
    }

    toast.dismiss(startedToastId)
    setSendingInvoices(false)
  }

  return (
    <div className="mt-2">
      <div className="mt-2 mb-4">
        <div className="row mr-0">
          <div className="col-3 d-flex align-items-center">
            <span className="mr-2">Frequency:</span>
            <select
              onChange={e => {
                let newBillingFrequency = e.target.value
                setBillingFrequency(newBillingFrequency)
                let newBillingCycleOptions = getBillingCycleOptions(
                  newBillingFrequency
                )
                setBillingCycleOptions(newBillingCycleOptions)
                setBillingCycleDateValue(newBillingCycleOptions[1])
              }}
              className="form-control"
              value={billingFrequency}
            >
              {[billingFrequencies.semimonthly, billingFrequencies.monthly].map(
                o => (
                  <option key={o} value={o}>
                    {_.startCase(o)}
                  </option>
                )
              )}
            </select>
          </div>
          <div className="col-4">
            <select
              className="form-control"
              value={billingCycleDateValue}
              onChange={e => setBillingCycleDateValue(e.target.value)}
            >
              {billingCycleOptions.map(o => (
                <option key={o} value={o}>
                  {getPrettyBillingRange(o)}
                </option>
              ))}
            </select>
          </div>
          <div className="col">
            <Dropdown
              className="input-group-prepend"
              trigger={
                <button
                  className="btn btn-secondary dropdown-toggle"
                  disabled={sendingInvoices}
                >
                  Send Invoices{' '}
                  {sendingInvoices && <i className="fa fa-spinner fa-spin" />}
                </button>
              }
            >
              {toggle => (
                <Fragment>
                  <div className="mx-3" onClick={() => 'ok'}>
                    All clients:{' '}
                    <button
                      className="btn btn-secondary ml-2"
                      onClick={e => {
                        if (!window.confirm('Ready to send the invoices?'))
                          return

                        toggle(e)
                        sendAllInvoices()
                      }}
                    >
                      Go
                    </button>
                  </div>
                  <div
                    className="mt-3 mx-3 d-flex align-items-center"
                    onClick={() => 'ok'}
                  >
                    Single client:
                    <div className="input-group" style={{ width: '160px' }}>
                      <input
                        className="form-control ml-2"
                        placeholder="Client ID"
                        value={clientId}
                        onChange={e => setClientId(e.target.value.trim())}
                      />
                      <div className="input-group-append">
                        <button
                          className="btn btn-secondary"
                          disabled={!clientId}
                          onClick={async e => {
                            setClientId('')
                            toggle(e)
                            setSendingInvoices(true)
                            try {
                              await sendSingleInvoice(clientId)
                            } catch (e) {
                              // do nothing. just stop
                            }
                            setSendingInvoices(false)
                          }}
                        >
                          Go
                        </button>
                      </div>
                    </div>
                  </div>
                </Fragment>
              )}
            </Dropdown>
            {reauthorizeLink && (
              <span className="ml-2 small">
                Quickbooks needs to be authorized:{' '}
                <a
                  href={reauthorizeLink || ''}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() => setReauthorizeLink('')}
                >
                  <i className="fa fa-external-link" /> Reauthorize
                </a>
              </span>
            )}
          </div>
        </div>
      </div>

      <h6 className="ml-1">Billing History</h6>
      <table className="table table-striped table-bordered table-sm small">
        <thead>
          <tr>
            <th>Billing Cycle</th>
            <th>Leads</th>
            <th>Avg price</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {_(billingHistory)
            .sortBy('id')
            .reverse()
            .value()
            .map(h => (
              <tr key={h.id}>
                <td>{getPrettyBillingRange(h.id)}</td>
                <td>{h.leadCount}</td>
                <td>${(h.totalAmount / h.leadCount).toFixed()}</td>
                <td>${h.totalAmount}</td>
              </tr>
            ))}
        </tbody>
      </table>
    </div>
  )
}

export default Billing
